import { useCallback, useEffect, useRef, useState } from "react";
import type { DocumentNode } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import SectionHeading from "@/components/SectionHeading";
import Table, { TableHeading, TableAction, TableFooter } from "@/components/Table";
import HtmlRichTextEditor from "@/components/RichTextEditor/HtmlRichTextEditor";
import CommentList from "@/components/CommentList";
import { toFormat } from "@/lib/dateHelpers";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";
import useUpdateTask from "@/pages/tasks/_hooks/useUpdateTask";
import type { CRMTask, CRMTaskChangelog } from "@/types";
import type { Reference } from "@/features/global/components/ReferencesTable/types";
import { getReferenceParentLink as baseGetReferenceParentLink } from "@/features/global/components/ReferencesTable";
import {
  BULK_DELETE_REFERENCES_MUTATION,
  TYPENAME_TO_REFERENCEABLE_TYPE,
  type BackofficeCRMBulkReferencesInput,
  type BulkDeleteReferencesResponse
} from "@/features/global/components/NotesTable/mutations";
import ReferencesTable from "@/features/global/components/ReferencesTable/ReferencesTable";
import useTaskComments from "../_hooks/useTaskComments";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";

interface ChangeHistoryRow {
  id: string;
  description: string;
  timestamp: string;
  modifiedBy: string;
}

const changeHistoryColumns: ColumnDef<ChangeHistoryRow, any>[] = [
  {
    accessorKey: "description",
    header: "Description",
    enableSorting: false,
    meta: { headerClassName: "hide-header-in-column" }
  },
  {
    accessorKey: "timestamp",
    header: "Timestamp",
    enableSorting: false
  },
  {
    accessorKey: "modifiedBy",
    header: "Modified By",
    enableSorting: false
  }
];

function toChangeHistoryRows(changelogs: CRMTaskChangelog[]): ChangeHistoryRow[] {
  return changelogs.map((entry) => ({
    id: String(entry.id),
    description: entry.description,
    timestamp: toFormat(entry.timestamp, "MMM dd, yyyy h:mm a") || "--",
    modifiedBy: entry.modifiedBy || "--"
  }));
}

interface TaskDetailsContentProps {
  task: CRMTask;
  refetchQueries?: DocumentNode[];
  onAddReference: () => void;
}

export default function TaskDetailsContent({ task, refetchQueries = [], onAddReference }: TaskDetailsContentProps) {
  const { trackEvent } = useAmplitude();

  const { comments, currentUser, onAddComment, onSaveEdit, onDelete } = useTaskComments(task, refetchQueries);

  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightReferenceId, setHighlightReferenceId] = useState<string | null>(null);

  useEffect(() => {
    const value = searchParams.get("highlightReference");
    if (!value) return;

    setSearchParams(
      (prev) => {
        prev.delete("highlightReference");
        return prev;
      },
      { replace: true }
    );
    setHighlightReferenceId(value);
  }, [searchParams, setSearchParams]);

  const getReferenceParentLink = useCallback(
    (ref: Reference) => {
      const link = baseGetReferenceParentLink(ref);
      if (!link) return null;
      if (ref.__typename === "CommerceOrder" || ref.__typename === "CommercePayment" || ref.__typename === "CRMEvent") {
        const separator = link.includes("?") ? "&" : "?";
        return `${link}${separator}highlightRow=${task.id}`;
      }
      return link;
    },
    [task.id]
  );

  const descriptionRef = useRef<string>(task.description || "");
  const baselineRef = useRef<string>("");
  const interactedRef = useRef(false);
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const { update, loading: saving, error: saveError } = useUpdateTask(task.id, { task, source: "task_detail" });

  const handleDescriptionChange = (html: string) => {
    descriptionRef.current = html;
    if (!interactedRef.current) {
      baselineRef.current = html;
      return;
    }
    setDescriptionDirty(html !== baselineRef.current);
  };

  const handleEditorFocus = () => {
    interactedRef.current = true;
  };

  const handleDescriptionSave = async () => {
    const saved = descriptionRef.current;
    const success = await update({ description: saved });
    if (success) {
      baselineRef.current = saved;
      setDescriptionDirty(descriptionRef.current !== saved);
    }
  };

  const changeHistoryData = toChangeHistoryRows(task.changelogs || []);

  const [deleteReferences] = useMutation<BulkDeleteReferencesResponse, { input: BackofficeCRMBulkReferencesInput }>(
    BULK_DELETE_REFERENCES_MUTATION,
    { refetchQueries }
  );

  const handleRemoveReference = useCallback(
    async (ref: Reference) => {
      const referenceableType = TYPENAME_TO_REFERENCEABLE_TYPE[ref.__typename];
      if (!referenceableType) return;

      try {
        const result = await deleteReferences({
          variables: {
            input: {
              referencedById: task.id,
              referencedByType: "TASK",
              references: [{ referenceableId: ref.id, referenceableType }]
            }
          }
        });
        if (result.data?.bulkDeleteReferences?.success) {
          trackEvent(TASK_ANALYTICS_EVENTS.referenceRemoved, {
            ...getTaskAnalyticsData(task, "task_detail"),
            referenceType: referenceableType
          });
          showSnackbar("Reference removed");
        } else {
          showErrorSnackbar("Failed to remove reference. Please try again.");
        }
      } catch {
        showErrorSnackbar("Failed to remove reference. Please try again.");
      }
    },
    [task, deleteReferences, trackEvent]
  );

  const referenceActions: TableAction[] = [
    {
      label: "Add Reference",
      variant: "text",
      icon: "add",
      onClick: onAddReference,
      testId: "add-task-reference-button"
    }
  ];

  return (
    <div className="flex flex-col">
      <div className="flex flex-col">
        <SectionHeading heading="Description" />
        {saveError && <Banner variant="error" description={saveError} inline closable={false} hideSecondaryDismiss />}
        <div className="relative" onFocusCapture={handleEditorFocus}>
          <HtmlRichTextEditor
            initialContent={task.description || ""}
            placeholder="Add a description to the task"
            hasOverlayButton={descriptionDirty}
            onChange={handleDescriptionChange}
          />
          {descriptionDirty && (
            <div className="absolute bottom-0 right-0 p-4">
              <Button variant="filled" onClick={handleDescriptionSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <ReferencesTable
        variant="top-level"
        references={task.references || []}
        tableHeading="Reference Information"
        tableBody="View other items in the system that relate to this task and/or provide additional context."
        actions={referenceActions}
        getParentLink={getReferenceParentLink}
        onRemoveReference={handleRemoveReference}
        highlightReferenceId={highlightReferenceId}
      />

      <CommentList
        comments={comments}
        heading="Comments"
        currentUser={currentUser}
        onAddComment={onAddComment}
        onSaveEdit={onSaveEdit}
        onDelete={onDelete}
      />

      <div>
        <TableHeading heading="Change History" />
        <Table
          data={changeHistoryData}
          columns={changeHistoryColumns}
          loading={false}
          renderFooter={() => <TableFooter totalRowCount={changeHistoryData.length} />}
        />
      </div>
    </div>
  );
}
